import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-[background-color,color,filter,box-shadow] duration-150 ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'btn-primary',
        destructive:
          'border border-destructive/80 bg-destructive text-destructive-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.18)] hover:bg-destructive/90',
        outline: 'btn-outline text-foreground hover:bg-accent hover:text-accent-foreground',
        secondary: 'btn-outline text-secondary-foreground hover:bg-accent',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-brand underline-offset-4 hover:underline',
      },
      // Touch-device bump: pointer-coarse: matches phones/tablets regardless of
      // viewport width (so an iPad in landscape still gets 44px targets).
      // Desktop pointer-fine devices keep the dense 36px/32px defaults.
      size: {
        default: 'h-9 px-4 py-2 pointer-coarse:h-11',
        sm: 'h-8 rounded-lg px-3 text-xs pointer-coarse:h-10',
        lg: 'h-10 rounded-lg px-6 pointer-coarse:h-12',
        icon: 'h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
